import React from 'react'
import styled from 'styled-components'

import useAnimatedNumber from 'utils/hooks/useAnimatedNumber'

const GAUGE_CENTER = 50
const GAUGE_RADIUS = 42
const GAUGE_STROKE = 6
const NEEDLE_HALF_WIDTH = 0.42
const NEEDLE_LENGTH = GAUGE_RADIUS - GAUGE_STROKE
const FILL_START_COMPASS = 0
const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

const polarToCartesian = (compassDeg, radius = GAUGE_RADIUS) => {
  const rad = ((compassDeg - 90) * Math.PI) / 180
  return {
    x: GAUGE_CENTER + radius * Math.cos(rad),
    y: GAUGE_CENTER + radius * Math.sin(rad),
  }
}

const describeArcPath = (startCompass, sweepDeg) => {
  if (sweepDeg <= 0) return ''
  const safeSweep = Math.min(sweepDeg, 359.999)
  const start = polarToCartesian(startCompass)
  const end = polarToCartesian(startCompass + safeSweep)
  const largeArcFlag = safeSweep > 180 ? 1 : 0
  return `M ${start.x} ${start.y} A ${GAUGE_RADIUS} ${GAUGE_RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`
}

const VisualContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  height: 100px;
  position: relative;
  width: 100px;
`

const Glow = styled.div`
  background: radial-gradient(circle, rgba(100, 136, 255, 0.18) 0%, rgba(100, 136, 255, 0) 72%);
  border-radius: 50%;
  inset: -12px -20px 0 10px;
  opacity: 0.6;
  position: absolute;
`

const GaugeSvg = styled.svg`
  display: block;
  height: 100%;
  inset: 0;
  overflow: visible;
  position: absolute;
  width: 100%;
`

const CupWrapper = styled.div`
  align-items: center;
  bottom: 22px;
  display: flex;
  justify-content: center;
  left: -18px;
  position: absolute;

  &::before {
    border-bottom: 6px solid var(--t-colors-yellow-400);
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;
    content: '';
    height: 0;
    left: 24px;
    position: absolute;
    top: 7px;
    transform: rotate(68deg);
    width: 0;
  }
`

const ChampionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.25 22C7.25 22.4142 7.58579 22.75 8 22.75L16 22.75C16.4142 22.75 16.75 22.4142 16.75 22C16.75 18.9892 14.7709 16.25 12 16.25C9.22912 16.25 7.25 18.9892 7.25 22Z"
      fill="#FFCA1B"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.5003 5C17.5003 4.44772 17.948 4 18.5003 4L22.0003 4C22.2902 4 22.5658 4.1258 22.7557 4.3448C22.9457 4.5638 23.0312 4.85444 22.9902 5.14142L22.7518 6.81038C22.7439 6.86585 22.736 6.92066 22.7283 6.97482C22.488 8.65922 22.3372 9.71622 21.9096 10.6449C21.4496 11.6441 20.7606 12.5207 19.8985 13.2038C19.0972 13.8388 18.1058 14.2352 16.5259 14.8668C16.4751 14.8871 16.4237 14.9077 16.3717 14.9285C15.8589 15.1336 15.2769 14.8842 15.0718 14.3714C14.8667 13.8586 15.1161 13.2766 15.6289 13.0715C17.4141 12.3575 18.1121 12.0676 18.6564 11.6363C19.2722 11.1484 19.7643 10.5222 20.0929 9.8085C20.3833 9.17772 20.5 8.43091 20.7719 6.52754L20.8473 6L18.5003 6C17.948 6 17.5003 5.55228 17.5003 5Z"
      fill="#FFCA1B"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.24455 4.3448C1.43449 4.1258 1.71012 4 2.00001 4L5.50001 4C6.05229 4 6.50001 4.44772 6.50001 5C6.50001 5.55228 6.05229 6 5.50001 6L3.15302 6L3.22838 6.52754C3.50029 8.43091 3.61696 9.17772 3.90738 9.8085C4.23595 10.5222 4.72809 11.1484 5.34387 11.6363C5.88814 12.0676 6.58622 12.3575 8.3714 13.0715C8.88418 13.2766 9.1336 13.8586 8.92848 14.3714C8.72337 14.8842 8.1414 15.1336 7.62862 14.9285C7.57659 14.9077 7.52519 14.8871 7.47439 14.8668C5.89451 14.2352 4.90311 13.8388 4.10177 13.2038C3.23967 12.5207 2.55068 11.6441 2.09067 10.6449C1.66309 9.71621 1.51228 8.65922 1.27195 6.97482C1.26422 6.92066 1.2564 6.86585 1.24848 6.81038L1.01006 5.14142C0.969058 4.85444 1.05462 4.5638 1.24455 4.3448Z"
      fill="#FFCA1B"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.76635 3.02234C4.73815 2.03481 5.54008 1.25 6.5018 1.25L17.5018 1.25C18.4635 1.25 19.2654 2.03481 19.2373 3.02234C19.1235 7.00516 18.3491 10.6098 17.1336 13.2461C15.9505 15.8123 14.2002 17.75 12.0018 17.75C9.80337 17.75 8.05314 15.8123 6.86998 13.2461C5.65452 10.6098 4.88005 7.00516 4.76635 3.02234Z"
      fill="#FFCA1B"
    />
  </svg>
)

function AwarenessCoverageGauge({ value = 0 }) {
  const safeValue = clamp(Math.round(value), 0, 100)
  const animatedValue = useAnimatedNumber(safeValue)
  const progress = animatedValue / 100
  const sweepDeg = progress * 360
  const needleAngle = FILL_START_COMPASS + sweepDeg
  const arcPath = describeArcPath(FILL_START_COMPASS, sweepDeg)
  const gradientStartPoint = polarToCartesian(FILL_START_COMPASS)
  const gradientEndAngle = progress >= 1 ? needleAngle - 0.1 : needleAngle
  const gradientEndPoint = polarToCartesian(gradientEndAngle)

  return (
    <VisualContainer data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_Gauge_div">
      <Glow />
      <GaugeSvg viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <linearGradient
            id="awarenessArcGradient"
            gradientUnits="userSpaceOnUse"
            x1={gradientStartPoint.x}
            y1={gradientStartPoint.y}
            x2={gradientEndPoint.x}
            y2={gradientEndPoint.y}
          >
            <stop offset="0%" stopColor="rgba(124, 147, 255, 0.25)" />
            <stop offset="60%" stopColor="#5A8EFF" />
            <stop offset="100%" stopColor="#3063FD" />
          </linearGradient>
        </defs>
        <circle
          cx={GAUGE_CENTER}
          cy={GAUGE_CENTER}
          fill="none"
          r={GAUGE_RADIUS}
          stroke="rgba(124, 147, 255, 0.18)"
          strokeWidth={GAUGE_STROKE}
        />
        <path
          d={arcPath}
          fill="none"
          stroke="url(#awarenessArcGradient)"
          strokeLinecap="round"
          strokeWidth={GAUGE_STROKE}
        />
        <g
          data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_Needle_g"
          transform={`rotate(${needleAngle} ${GAUGE_CENTER} ${GAUGE_CENTER})`}
        >
          <path
            d={`M ${GAUGE_CENTER - NEEDLE_HALF_WIDTH} ${GAUGE_CENTER} L ${GAUGE_CENTER} ${
              GAUGE_CENTER - NEEDLE_LENGTH
            } L ${GAUGE_CENTER + NEEDLE_HALF_WIDTH} ${GAUGE_CENTER} Z`}
            fill="#F2F5FE"
          />
          <circle cx={GAUGE_CENTER} cy={GAUGE_CENTER} fill="#3063FD" r="3.91" />
          <circle cx={GAUGE_CENTER} cy={GAUGE_CENTER} fill="#F2F5FE" r="1.83" />
        </g>
      </GaugeSvg>
      <CupWrapper>
        <ChampionIcon />
      </CupWrapper>
    </VisualContainer>
  )
}

export default AwarenessCoverageGauge
